import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { SEO, breadcrumbSchema } from "../components/SEO";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal } from "../components/Reveal";
import { PagePhoto } from "../components/PagePhoto";
import { pagePhotos } from "../content/photos";
import { FAQAccordion, faqSchema } from "../components/FAQAccordion";
import { CTASection } from "../components/CTASection";
import NotFound from "./NotFound";
import PriceCTA from "../components/PriceCTA";
import { trackEvent } from "../lib/analytics";
import { useLang, useLp, localeUrl } from "../i18n/lang";
import { useCopy } from "../i18n/copy";
import { useServices, useProblems, useGuides } from "../i18n/content";

const List = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-3 text-[15px] text-slate-700 leading-relaxed">
        <Check size={17} className="mt-1 shrink-0 text-precision" aria-hidden="true" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

const Block = ({ n, title, children }) => (
  <Reveal>
    <section className="border-t hairline pt-8 mt-12">
      <p className="font-mono text-xs text-terracotta tracking-widest mb-3">[{n}]</p>
      <h2 className="text-xl sm:text-2xl font-semibold text-ink mb-5">{title}</h2>
      {children}
    </section>
  </Reveal>
);

export default function ServicePage() {
  const { slug } = useParams();
  const services = useServices();
  const problems = useProblems();
  const guides = useGuides();
  const c = useCopy("servicePage");
  const cr = useCopy("crumbs");
  const cm = useCopy("common");
  const lp = useLp();
  const lang = useLang();
  const service = services.find((s) => s.slug === slug);
  if (!service) return <NotFound />;

  const crumbs = [
    { name: cr.home, path: lp("/") },
    { name: cr.services, path: lp("/servicios/") },
    { name: service.name, path: lp(`/servicios/${service.slug}/`) },
  ];
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.name,
    description: service.metaDescription,
    url: localeUrl(lang, `/servicios/${service.slug}/`),
    provider: { "@type": "Organization", name: "MideFinca", url: localeUrl(lang, "/") },
    areaServed: { "@type": "AdministrativeArea", name: "Canarias" },
  };

  return (
    <>
      <SEO
        title={service.seoTitle}
        description={service.metaDescription}
        path={`/servicios/${service.slug}/`}
        image={service.ogImage ? localeUrl("es", service.ogImage) : undefined}
        jsonLd={[breadcrumbSchema(crumbs), serviceSchema, ...(service.faq?.length ? [faqSchema(service.faq)] : [])]}
      />
      <Breadcrumbs items={crumbs} />
      <article className="container-site pb-8" data-testid={`service-page-${service.slug}`}>
        <header className="pt-10 max-w-3xl">
          <p className="tech-label mb-4">{service.category === "fincas" ? c.fincas : c.edif}</p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight">{service.h1}</h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed">{service.intro}</p>
          {service.officialLink && (
            <p className="mt-4 text-sm text-slate-600 leading-relaxed">
              {service.officialLink.label}{" "}
              <a href={service.officialLink.href} target="_blank" rel="noopener noreferrer" data-testid="service-official-link"
                 className="text-[#C56D42] underline underline-offset-2 hover:text-[#B45D3A] transition-colors break-all">
                {service.officialLink.href}
              </a>
            </p>
          )}
          <PagePhoto photo={pagePhotos[service.slug]} className="mt-10" />
          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to={lp(`/contacto/?necesidad=${encodeURIComponent(service.shortName)}`)}
              data-testid="service-cta-primary"
              onClick={() => trackEvent("cta_budget_click", { location: `service:${service.slug}` })}
              className="btn-primary"
            >
              {cm.tellCase} <ArrowRight size={16} aria-hidden="true" />
            </Link>
          </div>
        </header>

        <div className="max-w-3xl">
          <Block n="01" title={c.b1}>
            <List items={service.when} />
          </Block>
          <Block n="02" title={c.b2}>
            <p className="text-[15px] sm:text-base text-slate-700 leading-relaxed">{service.solves}</p>
          </Block>
          <Block n="03" title={c.b3}>
            <List items={service.includes} />
          </Block>
          <Block n="04" title={c.b4}>
            <List items={service.needs} />
          </Block>
          <Block n="05" title={c.b5}>
            <List items={service.receive} />
          </Block>
          <Reveal>
            <PriceCTA context={service.slug} />
            <p className="mt-5 text-sm text-slate-600">
              <Link to={lp("/precios/")} className="link-underline">{cm.priceDepLink}</Link>
            </p>
          </Reveal>
          {service.faq?.length > 0 && (
            <Block n="06" title={c.b6}>
              <FAQAccordion faqs={service.faq} testid={`service-faq-${service.slug}`} />
            </Block>
          )}
        </div>

        <Reveal className="mt-14 border-t hairline pt-10">
          <div className="grid md:grid-cols-3 gap-10">
            <div>
              <h2 className="tech-label mb-4">{cm.relatedServices}</h2>
              <ul className="space-y-2.5">
                {service.relatedServices.map((s) => {
                  const rel = services.find((x) => x.slug === s);
                  return rel ? (
                    <li key={s}>
                      <Link to={lp(`/servicios/${s}/`)} className="link-underline text-[15px]">{rel.shortName}</Link>
                    </li>
                  ) : null;
                })}
              </ul>
            </div>
            {service.relatedProblems.length > 0 && (
              <div>
                <h2 className="tech-label mb-4">{cm.relatedProblems}</h2>
                <ul className="space-y-2.5">
                  {service.relatedProblems.map((p) => {
                    const rel = problems.find((x) => x.slug === p);
                    return rel ? (
                      <li key={p}>
                        <Link to={lp(`/problemas/${p}/`)} className="link-underline text-[15px]">{rel.shortName}</Link>
                      </li>
                    ) : null;
                  })}
                </ul>
              </div>
            )}
            {service.relatedGuides.length > 0 && (
              <div>
                <h2 className="tech-label mb-4">{cm.relatedGuides}</h2>
                <ul className="space-y-2.5">
                  {service.relatedGuides.map((g) => {
                    const rel = guides.find((x) => x.slug === g);
                    return rel ? (
                      <li key={g}>
                        <Link to={lp(`/guias/${g}/`)} className="link-underline text-[15px]">{rel.title}</Link>
                      </li>
                    ) : null;
                  })}
                </ul>
              </div>
            )}
          </div>
        </Reveal>
      </article>
      <CTASection />
    </>
  );
}
